import { action, computed, observable, runInAction } from 'mobx';
import * as jwtDecode from 'jwt-decode';

import request from '../common/utils/Request';

import { JsonWebToken } from "../types";

const emptyJwt: JsonWebToken = {
  can_update: false,
  email: '',
  exp: NaN,
  first_name: '',
  is_mentor: false,
  last_name: '',
  orig_iat: NaN,
  user_id: NaN
}

class JwtStore {
  // Private instance
  private static _instance: JwtStore;
  
  @observable
  private rawJwt?: string;
  
  /**
   * Use JwtStore.instance to retrieve an instance of this class.
   */
  private constructor() {
    this.updateJwt();
  }

  /**
   * Instance of JwtStore.
   */
  public static get instance() {
    if (!this._instance) {
      this._instance = new JwtStore();
    }

    return this._instance;
  }

  /**
   * Local storage key for the JWT token.
   */
  public get localStorageKey(): string {
    return 'token';
  }

  /**
   * Get the raw JWT string.
   */
  @computed
  public get rawToken(): string | undefined {
    return this.rawJwt;
  }

  /**
   * Get parsed JWT object
   */
  @computed
  public get parsed(): JsonWebToken {
    return this.rawJwt ? jwtDecode(this.rawJwt) : emptyJwt;
  }


  /**
   * Get expiry date of the JWT.
   */
  @computed
  public get expiryDate(): number {
    return this.parsed.exp;
  }

  /**
   * Get issue date of the JWT.
   */
  @computed
  public get issuedAt(): number {
    return this.parsed.orig_iat;
  }
  
  /**
   * Make POST request to server and renew existing JWT.
   * 
   * @return Promise that resolves to nothing if successfully updated.
   */
  @action
  public async refresh(): Promise<void> {
    if (this.rawJwt) {
      try {
        const { data } = await request.post('/auth/jwt/refresh/', { token: this.rawJwt });
        const { token } = data;
        
        runInAction(() => {
          this.rawJwt = token;
          localStorage.setItem(this.localStorageKey, token);
        });
      } catch (error) {
        // Write to log
        runInAction(() => this.rawJwt = undefined);
      }
    }
  }

  /**
   * Update the cache with JWT from the local storage.
   * 
   * This method does not fetch new JWT from the cloud.
   * 
   * @return True if update successfully.
   */
  @action
  public updateJwt(): boolean {
    const token = localStorage.getItem(this.localStorageKey);
    if (token) {
      this.rawJwt = token;
      return true;
    }

    this.rawJwt = undefined;
    return false;
  }
}

export default JwtStore;
export const jwtStore = JwtStore.instance;


// WEBPACK FOOTER //
// ./src/stores/JwtStore.ts